import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SiteLayout from "../components/SiteLayout";
import { api } from "../lib/api";
import { ArrowUpRight, FileText } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function decodeEntities(s) {
  if (!s) return "";
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

function extractH2s(html) {
  if (!html) return [];
  const out = [];
  const re = /<h2[^>]*>(.*?)<\/h2>/gi;
  let m;
  while ((m = re.exec(html))) {
    const text = decodeEntities(m[1].replace(/<[^>]+>/g, ""));
    if (text) out.push(text);
  }
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get(`/blog/${slug}`);
        if (!cancelled) setPost(data);
      } catch (err) {
        if (!cancelled) {
          setPost(null);
          if (err?.response?.status === 404) setNotFound(true);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await api.get("/blog", { params: { limit: 6 } });
        if (!cancelled) setOthers((data || []).filter((p) => p.slug !== slug).slice(0, 3));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const toc = useMemo(() => (post ? extractH2s(post.body_html) : []), [post]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-6 py-24 space-y-6 animate-pulse">
          <div className="h-4 bg-surfaceAlt rounded w-24" />
          <div className="h-12 bg-surfaceAlt rounded w-4/5" />
          <div className="h-4 bg-surfaceAlt rounded w-1/3" />
          <div className="h-64 bg-surfaceAlt rounded-3xl mt-6" />
        </div>
      </SiteLayout>
    );
  }

  if (notFound || !post) {
    return (
      <SiteLayout>
        <Helmet>
          <title>Article not found | Zenith Ranking</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-4xl font-bold">Article not found</h1>
          <p className="text-inkSoft mt-3">The post you&apos;re looking for may have moved or been unpublished.</p>
          <Link to="/blog" className="btn-primary mt-6 inline-flex text-sm">Back to blog</Link>
        </div>
      </SiteLayout>
    );
  }

  const metaTitle = post.meta_title || post.title;
  const metaDesc = post.meta_description || post.excerpt || "";
  const canonical = post.canonical_url || `${siteUrl}/blog/${post.slug}`;
  const ogImg = post.og_image || post.cover_image_url || "";
  const published = post.published_at || "";
  const updated = post.updated_at || published;

  const ld = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: post.title,
    description: metaDesc,
    image: ogImg ? [ogImg] : [],
    datePublished: published,
    dateModified: updated,
    author: { "@type": "Person", name: post.author || "Zenith Ranking Editorial" },
    publisher: { "@type": "Organization", name: "Zenith Ranking", url: siteUrl },
  };

  return (
    <SiteLayout>
      <Helmet>
        <title>{`${metaTitle} | Zenith Ranking`}</title>
        <meta name="description" content={metaDesc} />
        <meta name="robots" content={post.robots || "index,follow"} />
        <link rel="canonical" href={canonical} />
        {(post.tags || []).length > 0 && <meta name="keywords" content={(post.tags || []).join(", ")} />}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Zenith Ranking" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonical} />
        {ogImg && <meta property="og:image" content={ogImg} />}
        {published && <meta property="article:published_time" content={published} />}
        {updated && <meta property="article:modified_time" content={updated} />}
        {post.author && <meta property="article:author" content={post.author} />}
        {post.category && <meta property="article:section" content={post.category} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        {ogImg && <meta name="twitter:image" content={ogImg} />}
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <article className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10">
          {/* Left rail: contents (desktop only) */}
          <aside className="hidden lg:block lg:col-span-3 lg:row-span-2">
            {toc.length > 0 && (
              <div className="sticky top-24 card-soft p-5">
                <div className="eyebrow mb-3">Contents</div>
                <ul className="space-y-2 text-sm">
                  {toc.map((t, i) => (
                    <li key={i} className="text-inkSoft hover:text-ink transition">{t}</li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          {/* Header + cover share the content column, so every left edge lines up */}
          <header className="lg:col-span-9 lg:col-start-4 min-w-0">
            <div className="flex items-center gap-2 mb-5">
              <span className="tag tag-accent">{post.category || "SEO"}</span>
              <span className="text-xs text-inkSoft">{post.reading_time || 3} min read</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="blog-post-title">{post.title}</h1>
            <p className="mt-4 text-inkSoft">{post.author} · {fmtDate(published)}</p>
            {post.cover_image_url && (
              <div className="mt-8 rounded-3xl overflow-hidden border border-line aspect-[16/8]">
                <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
              </div>
            )}
          </header>

          <div className="lg:col-span-9 lg:col-start-4 min-w-0">
            {post.excerpt && <p className="text-lg leading-relaxed text-ink/85 mb-6">{post.excerpt}</p>}
            <div
              className="blog-body text-base leading-relaxed text-ink/85"
              data-testid="blog-post-body"
              dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
            />
            <div className="card-soft p-7 mt-10 grain relative overflow-hidden">
              <div className="eyebrow mb-2">Take action</div>
              <h3 className="font-display text-2xl font-bold tracking-tight">Get a custom plan for your site.</h3>
              <p className="text-sm text-inkSoft mt-2">Book a 20-minute strategy call. Free. No upsell.</p>
              <Link to="/contact" className="btn-primary mt-5 text-sm">Book a strategy call <ArrowUpRight size={16} /></Link>
            </div>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section className="py-20 border-t border-line">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="eyebrow mb-3">Keep reading</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {others.map((b) => (
                <Link key={b.slug} to={`/blog/${b.slug}`} className="card-soft overflow-hidden group">
                  <div className="aspect-[16/10] overflow-hidden bg-surfaceAlt">
                    {b.cover_image_url ? (
                      <img src={b.cover_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-inkSoft"><FileText size={22} /></div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="tag tag-accent mb-2">{b.category || "SEO"}</div>
                    <h3 className="font-display text-lg font-semibold tracking-tight">{b.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        .blog-body { overflow-wrap: anywhere; word-break: normal; }
        .blog-body > * { max-width: 100%; }
        .blog-body h2 { font-family: 'Cabinet Grotesk', ui-serif; font-weight: 700; font-size: 1.75rem; margin-top: 2.5rem; margin-bottom: 0.75rem; letter-spacing: -0.01em; color: var(--ink); }
        .blog-body h3 { font-family: 'Cabinet Grotesk', ui-serif; font-weight: 700; font-size: 1.35rem; margin-top: 1.75rem; margin-bottom: 0.5rem; color: var(--ink); }
        .blog-body p { margin: 1rem 0; }
        .blog-body ul, .blog-body ol { padding-left: 1.5rem; margin: 1rem 0; }
        .blog-body ul { list-style: disc; }
        .blog-body ol { list-style: decimal; }
        .blog-body li { margin: 0.35rem 0; }
        .blog-body a { color: var(--terra); text-decoration: underline; text-underline-offset: 3px; }
        .blog-body a:hover { color: var(--terra-hover); }
        .blog-body blockquote { border-left: 3px solid var(--terra); padding: 0.25rem 0 0.25rem 1rem; margin: 1.25rem 0; color: var(--ink-soft); font-style: italic; }
        .blog-body img { border-radius: 16px; margin: 1.5rem 0; max-width: 100%; height: auto; border: 1px solid var(--line); }
        .blog-body pre, .blog-body code { font-family: 'JetBrains Mono', monospace; font-size: 0.9em; }
        .blog-body pre { background: var(--surface-alt); padding: 1rem; border-radius: 12px; overflow-x: auto; margin: 1.25rem 0; }
        .blog-body strong { color: var(--ink); }
        .blog-body table {
          width: 100%; border-collapse: collapse; margin: 1.75rem 0;
          font-size: 0.95rem; display: block; overflow-x: auto; white-space: normal;
        }
        .blog-body thead th, .blog-body tr:first-child td, .blog-body tr:first-child th {
          background: var(--surface-alt); color: var(--ink); font-weight: 700;
          text-align: left; letter-spacing: 0.01em;
        }
        .blog-body th, .blog-body td {
          border: 1px solid var(--line); padding: 0.7rem 0.9rem; vertical-align: top; min-width: 120px;
        }
        .blog-body tbody tr:nth-child(even) td { background: rgba(255,255,255,0.02); }
        .blog-body table a { word-break: break-word; }
      `}</style>
    </SiteLayout>
  );
}
